import { FormEvent, useEffect, useState } from "react";

import { api } from "../../api/client";
import { AlertBanner } from "../../components/feedback/AlertBanner";
import { AppLink } from '../../components/layout/AppLink';
import { Field } from '../../components/forms/Field';
import { FormModal } from '../../components/modals/FormModal';
import { AnimalQuickLookup } from '../../components/lookup/AnimalQuickLookup';
import { FormattedTags } from "../../components/tags/EarTag";
import { apiError } from "../../lib/apiError";
import { formatDate, formatDateTime, todayIso } from "../../lib/format";
import { DEFAULT_SPECIES, getSpecies } from "../../lib/species/species";
import { loadWithReplica, mutateOrQueue } from "../../lib/offline/helpers";
import { patchReplica } from "../../lib/offline/replica";
import type { DashboardAlert, DashboardSummary, Todo } from "../../types";

const species = getSpecies(DEFAULT_SPECIES);

const SEVERITY_LABEL: Record<DashboardAlert["severity"], string> = {
  critical: "Pilne",
  warning: "Ważne",
  info: "Info",
};

function isTodoOverdue(todo: Todo, today: string): boolean {
  return !todo.done && !!todo.due_date && todo.due_date < today;
}

export function DashboardPage() {
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [todoModalOpen, setTodoModalOpen] = useState(false);
  const [animalLookupOpen, setAnimalLookupOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [todoFilter, setTodoFilter] = useState<"active" | "completed">("active");

  async function load() {
    const result = await loadWithReplica<DashboardSummary>(
      "dashboard",
      () => api.GET("/api/dashboard")
    );
    if (result.error && !result.fromReplica) {
      setError(apiError(result.error, "Nie udało się wczytać pulpitu."));
      return;
    }
    setError(null);
    if (result.data) {
      setData(result.data);
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await loadWithReplica<DashboardSummary>(
        "dashboard",
        () => api.GET("/api/dashboard")
      );
      if (cancelled) {
        return;
      }
      if (result.error && !result.fromReplica) {
        setError(apiError(result.error, "Nie udało się wczytać pulpitu."));
        return;
      }
      if (result.data) {
        setData(result.data);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function resetTodoForm() {
    setTitle("");
    setDescription("");
    setDueDate("");
  }

  function closeTodoModal() {
    setTodoModalOpen(false);
    resetTodoForm();
  }

  async function onAddTodo(event: FormEvent) {
    event.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) {
      setError("Podaj tytuł zadania.");
      return;
    }
    setBusy(true);
    const result = await mutateOrQueue(
      "POST",
      "/api/todos",
      {
        title: trimmed,
        description: description.trim() || null,
        due_date: dueDate || null,
      }
    );
    setBusy(false);
    if (!result.success) {
      setError(apiError(result.error, "Nie udało się dodać zadania."));
      return;
    }
    if (!result.online) {
      const optimistic: Todo = {
        id: -Date.now(),
        title: trimmed,
        description: description.trim() || null,
        due_date: dueDate || null,
        done: false,
        created_at: new Date().toISOString(),
        completed_at: null,
      };
      await patchReplica<DashboardSummary>("dashboard", (current) => {
        if (!current) {
          return current;
        }
        return {
          ...current,
          todos: [...(current.todos ?? []), optimistic],
          kpi: {
            ...current.kpi,
            open_todos_count: (current.kpi?.open_todos_count ?? 0) + 1,
          },
        };
      });
    }
    closeTodoModal();
    await load();
  }

  async function toggleTodo(todo: Todo) {
    setBusy(true);
    const result = await mutateOrQueue(
      "PATCH",
      `/api/todos/${todo.id}`,
      { done: !todo.done, operation_id: crypto.randomUUID() }
    );
    setBusy(false);
    if (!result.success) {
      setError(apiError(result.error, "Nie udało się zaktualizować zadania."));
      return;
    }
    if (!result.online) {
      await patchReplica<DashboardSummary>("dashboard", (current) => {
        if (!current) {
          return current;
        }
        const nextDone = !todo.done;
        return {
          ...current,
          todos: (current.todos ?? []).map((item) =>
            item.id === todo.id
              ? {
                  ...item,
                  done: nextDone,
                  completed_at: nextDone ? new Date().toISOString() : null,
                }
              : item,
          ),
          kpi: {
            ...current.kpi,
            open_todos_count: Math.max(
              0,
              (current.kpi?.open_todos_count ?? 0) + (nextDone ? -1 : 1),
            ),
          },
        };
      });
    }
    await load();
  }

  async function removeTodo(todo: Todo) {
    setBusy(true);
    const result = await mutateOrQueue("DELETE", `/api/todos/${todo.id}`, { operation_id: crypto.randomUUID() });
    setBusy(false);
    if (!result.success) {
      setError(apiError(result.error, "Nie udało się usunąć zadania."));
      return;
    }
    if (!result.online) {
      await patchReplica<DashboardSummary>("dashboard", (current) => {
        if (!current) {
          return current;
        }
        const remaining = (current.todos ?? []).filter((item) => item.id !== todo.id);
        const openDelta = todo.done ? 0 : -1;
        return {
          ...current,
          todos: remaining,
          kpi: {
            ...current.kpi,
            open_todos_count: Math.max(0, (current.kpi?.open_todos_count ?? 0) + openDelta),
          },
        };
      });
    }
    await load();
  }

  const kpi = data?.kpi;
  const alerts = data?.alerts ?? [];
  const todos = data?.todos ?? [];
  const openTodos = todos.filter((todo) => !todo.done);
  const completedTodos = todos.filter((todo) => todo.done);
  const filteredTodos =
    todoFilter === "active"
      ? openTodos
      : completedTodos;
  const today = todayIso();
  const attentionCount = kpi?.attention_count ?? alerts.length;
  const hasCritical = alerts.some((alert) => alert.severity === "critical");
  const hasWarning = alerts.some((alert) => alert.severity === "warning");
  const attentionKpiClass = [
    "kpi__card",
    attentionCount > 0
      ? hasCritical
        ? "kpi__card--attention-critical"
        : "kpi__card--attention"
      : "",
  ]
    .filter(Boolean)
    .join(" ");
  const attentionPanelClass = [
    "panel",
    "panel--attention",
    hasCritical ? "panel--attention-critical" : hasWarning ? "panel--attention-warn" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <main className="page" data-testid="dashboard-page">
      <header className="page__header">
        <p className="page__kicker">Pulpit</p>
      </header>

      <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />

      <section className="kpi" aria-label="Podsumowanie">
        <AppLink href="/animals" className="kpi__card">
          <p className="kpi__label">Zwierzęta w stadzie</p>
          <p className="kpi__value">{kpi ? kpi.animals_count : "—"}</p>
        </AppLink>
        <a href="#dashboard-alerts" className={attentionKpiClass}>
          <p className="kpi__label">Wymaga uwagi</p>
          <p className="kpi__value">{kpi ? kpi.attention_count : "—"}</p>
        </a>
        <a href="#dashboard-todos" className="kpi__card">
          <p className="kpi__label">Otwarte zadania</p>
          <p className="kpi__value">{kpi ? kpi.open_todos_count : "—"}</p>
        </a>
        <AppLink href="/statistics" className="kpi__card kpi__card--action">
          <p className="kpi__label">Statystyki</p>
          <p className="kpi__value kpi__value--action">Więcej</p>
        </AppLink>
      </section>

      <section className="panel" aria-label="Szybkie akcje">
        <h2 className="panel__title">Szybkie akcje</h2>
        <div className="actions">
          <button
            type="button"
            className="chip chip--action"
            data-testid="sheep-quick-lookup-open"
            onClick={() => setAnimalLookupOpen(true)}
          >
            {species.labels.lookup_title}
          </button>
          <AppLink href="/reproduction/breeding" className="chip">
            Stanówka
          </AppLink>
          <AppLink href="/reproduction/births" className="chip">
            Nowy wykot
          </AppLink>
          <AppLink href="/expenses" className="chip">
            Dodaj transakcję
          </AppLink>
        </div>
      </section>

      <section
        className={attentionPanelClass}
        id="dashboard-alerts"
        aria-label="Do zrobienia teraz"
        data-testid="dashboard-alerts"
      >
        <div className="panel__header">
          <div>
            <h2 className="panel__title panel__title--flush">Do zrobienia teraz</h2>
            <p className="panel__lead">
              Najważniejsze sprawy w stadzie — zacznij od nich.
            </p>
          </div>
          {data && attentionCount > 0 ? (
            <span
              className={
                hasCritical
                  ? "attention__count attention__count--critical"
                  : "attention__count attention__count--warning"
              }
            >
              {attentionCount}
            </span>
          ) : null}
        </div>
        {!data ? (
          <p className="muted">Wczytywanie…</p>
        ) : alerts.length === 0 ? (
          <p className="muted">Brak pilnych spraw — wszystko na bieżąco.</p>
        ) : (
          <ul className="attention__list">
            {alerts.map((alert) => (
              <li
                key={alert.id}
                className={`attention__item attention__item--${alert.severity}`}
              >
                <div className="attention__body">
                  <span className={`attention__badge attention__badge--${alert.severity}`}>
                    {SEVERITY_LABEL[alert.severity]}
                  </span>
                  <p className="attention__title">
                    <FormattedTags text={alert.title} />
                  </p>
                  {alert.detail ? (
                    <p className="attention__detail">
                      <FormattedTags text={alert.detail} />
                    </p>
                  ) : null}
                </div>
                <AppLink href={alert.href} className="btn btn--sm attention__action">
                  {alert.action_label}
                </AppLink>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="split">
        <section className="panel" id="dashboard-todos">
          <div className="panel__header">
            <h2 className="panel__title panel__title--flush">Moje zadania</h2>
            <button
              type="button"
              className="btn"
              onClick={() => {
                resetTodoForm();
                setTodoModalOpen(true);
              }}
            >
              Dodaj zadanie
            </button>
          </div>
          {todos.length === 0 ? (
            <p className="muted">Brak zadań — dodaj pierwsze przyciskiem powyżej.</p>
          ) : (
            <>
              <nav className="tabs" aria-label="Filtr zadań">
                <button
                  type="button"
                  className={todoFilter === "active" ? "tab tab--active" : "tab"}
                  data-testid="dashboard-todos-filter-active"
                  onClick={() => setTodoFilter("active")}
                >
                  Aktywne ({openTodos.length})
                </button>
                <button
                  type="button"
                  className={todoFilter === "completed" ? "tab tab--active" : "tab"}
                  data-testid="dashboard-todos-filter-completed"
                  onClick={() => setTodoFilter("completed")}
                >
                  Zakończone ({completedTodos.length})
                </button>
              </nav>
              {filteredTodos.length === 0 ? (
                <p className="muted">
                  {todoFilter === "active"
                    ? "Brak aktywnych zadań."
                    : "Brak zakończonych zadań."}
                </p>
              ) : (
                <ul className="list list--scroll" data-testid="dashboard-todos-list">
                  {filteredTodos.map((todo) => {
                    const overdue = isTodoOverdue(todo, today);
                    const itemClass = [
                      "list__item",
                      todo.done ? "list__item--done" : "",
                      overdue ? "list__item--overdue" : "",
                    ]
                      .filter(Boolean)
                      .join(" ");
                    return (
                      <li key={todo.id} className={itemClass}>
                        <div>
                          <strong>
                            <FormattedTags text={todo.title} />
                          </strong>
                          {todo.due_date ? (
                            <span className={overdue ? "list__due list__due--overdue" : "muted"}>
                              {" "}
                              · {overdue ? "Po terminie · " : null}
                              {formatDate(todo.due_date)}
                            </span>
                          ) : null}
                          {todo.description ? <p className="muted">{todo.description}</p> : null}
                        </div>
                        <div className="list__actions">
                          {!todo.done ? (
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              disabled={busy}
                              onClick={() => toggleTodo(todo)}
                            >
                              Zakończ
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            disabled={busy}
                            onClick={() => removeTodo(todo)}
                          >
                            Usuń
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </section>

        <section className="panel">
          <h2 className="panel__title">Ostatnia aktywność</h2>
          {!data ? (
            <p className="muted">Wczytywanie…</p>
          ) : data.activity.length === 0 ? (
            <p className="muted">Brak aktywności.</p>
          ) : (
            <ul className="list list--scroll">
              {data.activity.map((item, index) => (
                <li key={`${item.kind}-${item.at}-${index}`} className="list__item">
                  <div className="list__body">
                    <AppLink href={item.href} className="btn--link">
                      <FormattedTags text={item.title} />
                    </AppLink>
                    {item.detail ? (
                      <p className="muted">
                        <FormattedTags text={item.detail} />
                      </p>
                    ) : null}
                  </div>
                  <time className="list__meta muted" dateTime={item.at}>
                    {formatDateTime(item.at)}
                  </time>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <FormModal
        open={todoModalOpen}
        onClose={closeTodoModal}
        title="Nowe zadanie"
        testId="todo-form"
        onSubmit={onAddTodo}
      >
        <Field label="Tytuł">
          <input
            className="input"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Np. jutro ważenie…"
            maxLength={200}
            required
            autoFocus
          />
        </Field>
        <Field label="Opis (opcjonalnie)">
          <textarea
            className="input"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Dodatkowe szczegóły…"
            maxLength={500}
            rows={3}
          />
        </Field>
        <Field label="Termin (opcjonalnie)">
          <input
            className="input"
            type="date"
            value={dueDate}
            onChange={(event) => setDueDate(event.target.value)}
          />
        </Field>
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={closeTodoModal} disabled={busy}>
            Anuluj
          </button>
          <button className="btn" type="submit" disabled={busy}>
            Dodaj
          </button>
        </div>
      </FormModal>

      <AnimalQuickLookup
        open={animalLookupOpen}
        onClose={() => setAnimalLookupOpen(false)}
        onTodosChanged={() => void load()}
      />
    </main>
  );
}
